import React, { useLayoutEffect, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, Link, useLocation } from "react-router-dom";
import { StatusCodes } from "http-status-codes";
import fleetlogicAPI from "helpers/fleetlogicAPI";
import { jwtDecode } from "jwt-decode";
import i18n from "i18n/i18n";
import { AuthorizationToken } from "helpers/AuthorizationToken";
import {
  AUTH_URL,
  APP_BASE_PATH,
  APP_DEFAULT_REDIRECT_URL,
  TIS_REGISTER_URL,
  HEADOFFICE_OPTION_VALUE
} from "constants/AppConstants";
import { Button } from "@texada/ui";
import isEmpty from "lodash/isEmpty";
import DnaAnimation from "./DnaAnimation";
import store from "../../reduxstore/reduxStore";
import { logout } from "../../actions";
import LoginInfoDisplay from "./LoginInfoDisplay";
import LoginContext from "./LoginContext";
import {
  getLoginError,
  setDocDivisionAccess,
  setTokens,
  handleNewLoginResponse,
  handleOldLoginResponse,
  getTisRegistrationUrl
} from "./loginUtils";
import { getCompressedItem } from "helpers/compressedStorage";
import "./MobileLogin.scss";

const MobileLogin = () => {
  const { token: mobile_session_token } = useParams();
  const location = useLocation();
  const { login, loginAuth, error: loginError } = useContext(LoginContext);
  const user = useSelector(state => state?.auth?.user);
  const query = new URLSearchParams(location.search);
  const currentUser = JSON.parse(getCompressedItem("currentUser"));
  const isLoggedIn = useSelector(state => state.auth.isAuthenticated);
  const [error, setError] = useState("");

  useLayoutEffect(() => {
    try {
      const userData = jwtDecode(mobile_session_token);
      const oldToken = userData.tis_session ? "" : mobile_session_token;
      loginAuth(
        userData.tenant_id,
        undefined,
        userData.tis_session,
        userData.company_sso_enabled,
        userData.email,
        oldToken
      )
        .then(() => {
          setDocDivision();
          localStorage.setItem("verified", true);
        })
        .catch(e => {
          if (!userData.tis_session) {
            AuthorizationToken.set(mobile_session_token);
            setAuth(userData);
            return;
          }
          if (e.response?.status !== StatusCodes.BAD_REQUEST) console.error(e);
        });
    } catch (err) {
      if (isLoggedIn && user.is_tis) {
        handleUserCompanyChange();
        return;
      }
      loginWithMobileSession();
    }
  }, []);

  const handleUserCompanyChange = async () => {
    const response = await fleetlogicAPI.post(AUTH_URL, {
      mobile_session_token
    });
    if (user.email !== response.data.user.email) {
      store.dispatch(logout());
      return;
    }
    if (user.tenant_id !== response.data.user.tenant_id) {
      window.location.href = `${APP_BASE_PATH}/instance/${response.data.user.tenant_id}?token=${localStorage.jwtToken}`;
      return;
    }
    window.location.href = APP_DEFAULT_REDIRECT_URL;
  };

  const loginWithMobileSession = () => {
    localStorage.clear();
    login({
      mobile_session_token
    })
      .then(() => {
        setDocDivision();
        localStorage.setItem("verified", true);
      })
      .catch(e => {
        if (e.response?.status !== StatusCodes.BAD_REQUEST) console.error(e);
        setError({
          message: i18n.t(
            "invalid_credentials_your_account_will_be_locked_out_after_5_failed_attempts_please_try_again_or_check_your_email"
          )
        });
      });
  };

  const setAuth = async userData => {
    const response = await fleetlogicAPI.get(
      `/rest-api/company/${userData.company_id}/user/${userData.srm_uid}?include_flask=true&include_token=true`
    );
    await setTokens(response.data);
    localStorage.setItem("verified", true);
    if (response.data.upgraded) {
      await handleNewLoginResponse(response);
    } else {
      await handleOldLoginResponse(response);
    }
    await setDocDivision();
  };

  const redirectPage = async () => {
    if (isEmpty(JSON.parse(getCompressedItem("currentUser")))) {
      return;
    }

    let page = APP_DEFAULT_REDIRECT_URL;
    const redirectTo = query.get("page");
    const url = await getTisRegistrationUrl(
      JSON.parse(getCompressedItem("currentUser")),
      false,
      true
    );
    if (redirectTo && (redirectTo !== TIS_REGISTER_URL || url)) {
      page = `/${redirectTo}`;
    }

    try {
      window.location.href = page;
    } catch (err) {
      console.error(err);
      window.location.href = APP_DEFAULT_REDIRECT_URL;
    }
  };

  const setDocDivision = async () => {
    try {
      await setDocDivisionAccess(
        user,
        query.get("division") || HEADOFFICE_OPTION_VALUE
      );
      redirectPage();
    } catch (err) {}
  };

  const errorMsg = getLoginError(error || loginError);
  return (
    <div className="mobile-login">
      {errorMsg ? (
        <>
          <LoginInfoDisplay errorMsg={errorMsg} />
          <Button
            as={Link}
            className="login-button"
            buttonProps={{
              to: "/"
            }}
          >
            Go to Login
          </Button>
        </>
      ) : (
        <>
          <DnaAnimation />
          <p>Connecting...</p>
        </>
      )}
    </div>
  );
};

export default MobileLogin;
